import { TableColumnsType } from 'antd';
import TitleBar from '../../../components/TitleBar';
import { Footer } from '../../../components/common/Footer';

import './SummarySuppliers.css';
import { removePlusSignSuffix } from '../../../utils/removePlusSignSuffix';
import { useQuery } from '@apollo/client';
import { FETCH_SUPPLIER_SUMMARY } from '../queries/Summary';
import { camelCaseToDash } from '../../../utils/camelCaseToDash';
import Filters from '../../../components/Filters/Filters';
import { useSearchParams } from 'react-router-dom';
import { tableSortingMethods } from '../../../utils/tableSortingMethods';
import TableV3 from '../../../components/generic/Table/TableV3';
import FinancialData from '../../../components/common/FinancialData';
import { formatSupplierData } from '../../../utils';
import { useUserContext } from '../../../context/PermissionsContext';
import QueryErrorBoundary from '../../../components/error-handling';
import { startCase } from 'lodash';
import { ExportColumn } from '../../../interfaces/ExportColumn';
import TitleUpdater from '../../../components/common/TitleUpdater';

type SupplierSummaryType = {
  uuid: React.Key;
  supplierName: string;
  sourcingStatus: string;
  availableAlternatives: string;
  qualifiedAlternatives: string;
  bcpUploaded: boolean;
  bcpUpdatedAt: string;
  totalRevenueSupported: any;
};

function removeCurrencyAndMagnitude(value: string) {
  let formattedValue;
  formattedValue = String(value).replace(/\$/g, '');
  formattedValue = formattedValue.replace(/M/g, '');
  return Number(formattedValue);
}

interface DataReturned {
  getSupplierDependenciesSummary: SupplierSummaryType[];
}

const SummarySuppliers = () => {
  let [searchParams] = useSearchParams();
  const locationUuid = searchParams.get('location');
  const divisionUuid = searchParams.get('division');

  const { canViewFinancials } = useUserContext();

  const { data, loading, error } = useQuery(FETCH_SUPPLIER_SUMMARY, {
    variables: { filter: { locationUuid, divisionUuid } },
  });

  const suppliersSummaryData = formatSupplierData(
    data?.getSupplierDependenciesSummary ?? [],
  );

  const customColumns: TableColumnsType<any> = [
    {
      key: 'supplierName',
      title: 'Supplier',
      width: 200,
      sorter: (a, b) =>
        tableSortingMethods.string(a.supplierName, b.supplierName),
      render: ({ supplierName }) => (
        <span className="custom-table--column">
          {startCase(supplierName.toLowerCase())}
        </span>
      ),
    },
    {
      key: 'sourcingStatus',
      title: 'Sourcing Status',
      width: 150,
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.string(a.sourcingStatus, b.sourcingStatus),
      render: ({ sourcingStatus }: any) => (
        <span className="custom-table--column">
          {camelCaseToDash(sourcingStatus)}
        </span>
      ),
    },
    {
      key: 'availableAlternatives',
      title: 'Available Alternatives',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(
          Number(removePlusSignSuffix(a.availableAlternatives)),
          Number(removePlusSignSuffix(b.availableAlternatives)),
        ),
      render: ({ availableAlternatives }: any) => (
        <span className="custom-table--column">{availableAlternatives}</span>
      ),
    },
    {
      key: 'qualifiedAlternatives',
      title: 'Qualified Alternatives',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(
          Number(removePlusSignSuffix(a.qualifiedAlternatives)),
          Number(removePlusSignSuffix(b.qualifiedAlternatives)),
        ),
      render: ({ qualifiedAlternatives }: any) => (
        <span className="custom-table--column">{qualifiedAlternatives}</span>
      ),
    },
    {
      key: 'bcpUploaded',
      title: 'BCP Added',
      width: 100,
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.boolean(a.bcpUploaded, b.bcpUploaded),
      render: ({ bcpUploaded }: any) => (
        <span className="custom-table--column">
          {bcpUploaded ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      key: 'bcpUpdatedAt',
      title: 'BCP Date',
      width: 150,
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.date(a.bcpUpdatedAt, b.bcpUpdatedAt),
      render: ({ bcpUpdatedAt }: any) => (
        <span className="custom-table--column">
          {bcpUpdatedAt?.slice(0, 10) || (
            <span className="placeholder-text">No available date</span>
          )}
        </span>
      ),
    },
    {
      key: 'totalRevenueSupported',
      title: 'Total Revenue Supported',
      width: 100,
      align: 'right',
      sorter: canViewFinancials
        ? (a, b) =>
            tableSortingMethods.number(
              removeCurrencyAndMagnitude(a.totalRevenueSupported),
              removeCurrencyAndMagnitude(b.totalRevenueSupported),
            )
        : undefined,
      render: ({ totalRevenueSupported }: any) => (
        <span className="custom-table--column custom-table--column-revenue">
          {<FinancialData text={totalRevenueSupported} />}
        </span>
      ),
    },
  ];

  const baseColumns: ExportColumn[] = [
    {
      key: 'supplierName',
      header: 'Supplier',
      type: 'text',
      width: 35,
    },
    {
      key: 'sourcingStatus',
      header: 'Sourcing Status',
      type: 'text',
      width: 30,
    },
    {
      key: 'availableAlternatives',
      header: 'Available Alternatives',
      type: 'text',
      width: 30,
    },
    {
      key: 'qualifiedAlternatives',
      header: 'Qualified Alternatives',
      type: 'text',
      width: 30,
    },
    {
      key: 'bcpUploaded',
      header: 'BCP Added',
      type: 'boolean',
      width: 30,
    },
    {
      key: 'bcpUpdatedAt',
      header: 'BCP Date',
      type: 'text',
      placeholder: 'No available date',
      width: 30,
    },
    {
      key: 'totalRevenueSupported',
      header: 'Total Revenue Supported',
      type: 'money',
      width: 35,
    },
  ];

  return (
    <section>
      <TitleUpdater title={`Suppliers Exposure Summary`} />
      <TitleBar title="Exposure Summary" subtitle="Supplier View" />
      <Filters showFilters={['location', 'division']} />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Suppliers Summary"
          columns={customColumns}
          data={suppliersSummaryData}
          loading={loading}
          customStyles="custom-column-PA--heading custom-table--suppliers-exposure-summary"
          exportExcelColumns={baseColumns}
        />
      </QueryErrorBoundary>
      <Footer backPath={'/exposures'} />
    </section>
  );
};

export default SummarySuppliers;
